/**
 * CallSummaryPanel (AI-INTAKE-SUMMARY) — "Summarize the call" on a dispatch that
 * came from a recording. Calls `summarize-intake` (Gemini listens to the audio),
 * then PROPOSES: the summary + pulled details go into the dispatch notes only
 * when the office clicks "Use as notes," and any price heard is a separate
 * CONFIRM ("Set as dispatch price") — never auto-applied, because a transcript
 * can mishear a number (Seth 2026-06-25, [[feedback_review_before_commit]]).
 */
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Sparkles, Loader2, Check, DollarSign } from "lucide-react";
import { supabase } from "@/lib/supabase";

interface IntakeFields {
  symptoms: string;
  equipment: string;
  access: string;
  urgency: string;
  quoted_price: number | null;
  promised_window: string;
}
interface IntakeResult {
  ok: boolean;
  summary: string;
  fields: IntakeFields;
  error?: string;
}

/** Compose the proposed notes block from the summary + any non-empty details. */
function composeNotes(r: IntakeResult): string {
  const f = r.fields;
  const extra: string[] = [];
  if (f.symptoms) extra.push(`Symptoms: ${f.symptoms}`);
  if (f.equipment) extra.push(`Equipment: ${f.equipment}`);
  if (f.access) extra.push(`Access: ${f.access}`);
  if (f.promised_window) extra.push(`Promised: ${f.promised_window}`);
  if (f.quoted_price != null) extra.push(`Quoted on call: $${f.quoted_price}`);
  return extra.length ? `${r.summary}\n\n${extra.join("\n")}` : r.summary;
}

export function CallSummaryPanel({
  callId,
  onUseNotes,
  onUsePrice,
}: {
  callId: string;
  onUseNotes: (text: string) => void;
  onUsePrice?: (price: number) => void;
}) {
  const [result, setResult] = useState<IntakeResult | null>(null);
  const [usedNotes, setUsedNotes] = useState(false);
  const [usedPrice, setUsedPrice] = useState(false);

  const run = useMutation({
    mutationFn: async (): Promise<IntakeResult> => {
      const { data, error } = await supabase.functions.invoke("summarize-intake", { body: { callId } });
      if (error) throw error;
      return data as IntakeResult;
    },
    onSuccess: (d) => {
      setUsedNotes(false);
      setUsedPrice(false);
      setResult(d);
    },
  });

  if (!result) {
    return (
      <button
        type="button"
        onClick={() => run.mutate()}
        disabled={run.isPending}
        className="inline-flex items-center gap-1.5 rounded-md border border-brand-200 bg-brand-50 px-2.5 py-1.5 text-xs font-semibold text-brand-700 transition-colors hover:bg-brand-100 disabled:opacity-60"
      >
        {run.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
        {run.isPending ? "Listening to the call…" : "Summarize the call"}
      </button>
    );
  }

  if (!result.ok) {
    return (
      <div className="text-xs text-amber-600">
        Couldn&rsquo;t summarize the call{result.error ? ` (${result.error})` : ""}.{" "}
        <button type="button" onClick={() => setResult(null)} className="font-medium underline">
          Try again
        </button>
      </div>
    );
  }

  const f = result.fields;
  const detail = (label: string, val: string) =>
    val ? (
      <div key={label}>
        <span className="font-medium text-slate-500">{label}:</span> <span className="text-slate-700">{val}</span>
      </div>
    ) : null;
  const hasDetails = f.symptoms || f.equipment || f.access || f.urgency || f.promised_window;

  return (
    <div className="rounded-lg border border-brand-200 bg-brand-50/40 p-2.5 text-xs">
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand-700">
        <Sparkles className="h-3.5 w-3.5" /> AI summary of the call · review before applying
      </div>
      <p className="whitespace-pre-wrap text-slate-800">{result.summary}</p>

      {hasDetails && (
        <div className="mt-1.5 space-y-0.5 border-t border-brand-200/60 pt-1.5">
          {detail("Symptoms", f.symptoms)}
          {detail("Equipment", f.equipment)}
          {detail("Access", f.access)}
          {detail("Urgency", f.urgency)}
          {detail("Promised", f.promised_window)}
        </div>
      )}

      {f.quoted_price != null && (
        <div className="mt-1.5 flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5">
          <span className="text-amber-800">
            <DollarSign className="mr-0.5 inline h-3.5 w-3.5 align-[-2px]" />
            Heard <span className="font-semibold">${f.quoted_price}</span> quoted on the call
          </span>
          {onUsePrice &&
            (usedPrice ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
                <Check className="h-3.5 w-3.5" /> Applied
              </span>
            ) : (
              <button
                type="button"
                onClick={() => { onUsePrice(f.quoted_price!); setUsedPrice(true); }}
                className="shrink-0 rounded bg-amber-600 px-2 py-0.5 text-[11px] font-semibold text-white transition-colors hover:bg-amber-700"
              >
                Set as dispatch price
              </button>
            ))}
        </div>
      )}

      <div className="mt-2 flex items-center justify-end gap-1.5">
        <button type="button" onClick={() => setResult(null)} className="rounded-md px-2 py-1 text-[11px] text-slate-500 transition-colors hover:bg-white">
          Dismiss
        </button>
        {usedNotes ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-emerald-700">
            <Check className="h-3.5 w-3.5" /> Added to notes — review &amp; save
          </span>
        ) : (
          <button
            type="button"
            onClick={() => { onUseNotes(composeNotes(result)); setUsedNotes(true); }}
            className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-brand-800"
          >
            <Check className="h-3.5 w-3.5" /> Use as notes
          </button>
        )}
      </div>
    </div>
  );
}
